import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, X } from 'lucide-react';
import { CART_FINALIZE_PROMPT_TEST_ID, CART_FINALIZE_STICKY_CLASS } from './cartFinalizeUx';
import { CART_ADD_ANOTHER_SLOT_TEST_ID } from './cartAnotherSlotUx';
import './CartFinalizePrompt.css';

export type CartFinalizePromptProps = {
  itemCount: number;
  highlighted: boolean;
  onViewCart: () => void;
  showAddAnotherSlot?: boolean;
  onAddAnotherSlot?: () => void;
  /** Dans la pile du panier : le rappel n'est plus positionné tout seul. */
  stacked?: boolean;
  /** Ferme le rappel jusqu'au prochain ajout au panier. */
  onDismiss?: () => void;
};

export function CartFinalizePrompt({
  itemCount,
  highlighted,
  onViewCart,
  showAddAnotherSlot = false,
  onAddAnotherSlot,
  stacked = false,
  onDismiss,
}: CartFinalizePromptProps) {
  const { t } = useTranslation('booking');
  const navigate = useNavigate();

  const handleFinalize = () => {
    navigate('/checkout');
  };

  return (
    <div
      data-testid={CART_FINALIZE_PROMPT_TEST_ID}
      className={[
        CART_FINALIZE_STICKY_CLASS,
        stacked ? 'cart-finalize-prompt--stacked' : '',
        'cart-finalize-prompt',
        highlighted ? 'cart-finalize-prompt--highlight' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      role='region'
      aria-live='polite'
      aria-label={t('cart.finalizeRegionAria')}
    >
      <div className='cart-finalize-prompt__panel'>
        {onDismiss ? (
          <button
            type='button'
            className='cart-finalize-prompt__close'
            aria-label={t('cart.dismissFinalize')}
            onClick={onDismiss}
          >
            <X size={16} aria-hidden='true' />
          </button>
        ) : null}
        <p className='cart-finalize-prompt__message flex items-start gap-2'>
          <AlertCircle className='w-4 h-4 flex-shrink-0 mt-0.5 text-[#a5765c]' aria-hidden />
          <span>{t('cart.bookingNotComplete')}</span>
        </p>
        <p className='cart-finalize-prompt__meta'>{t('cart.itemsInCart', { count: itemCount })}</p>
        <div className='cart-finalize-prompt__actions'>
          <button type='button' className='cart-finalize-prompt__cta' onClick={handleFinalize}>
            {t('cart.finalizeBooking')}
          </button>
          {showAddAnotherSlot && onAddAnotherSlot ? (
            <button
              type='button'
              data-testid={CART_ADD_ANOTHER_SLOT_TEST_ID}
              className='cart-finalize-prompt__another-slot'
              onClick={onAddAnotherSlot}
            >
              {t('cart.addAnotherSlotSameService')}
            </button>
          ) : null}
          <button type='button' className='cart-finalize-prompt__secondary' onClick={onViewCart}>
            {t('cart.viewCart')}
          </button>
        </div>
      </div>
    </div>
  );
}
